import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import BodyPartTemplateAdminSection from './BodyPartTemplateAdminSection.jsx';
import PainTemplateAdminSection from './PainTemplateAdminSection.jsx';
import RomTemplateAdminSection from './RomTemplateAdminSection.jsx';
import StrengthTemplateAdminSection from './StrengthTemplateAdminSection.jsx';
import GoalTemplateAdminSection from './GoalTemplateAdminSection.jsx';
import SpecialTestTemplateAdminSection from './SpecialTestTemplateAdminSection.jsx';
import PalpationTemplateAdminSection from './PalpationTemplateAdminSection.jsx';
import AggravatingFactorTemplateAdminSection from './AggravatingFactorTemplateAdminSection.jsx';
import ClinicalContentTemplateAdminSection from './ClinicalContentTemplateAdminSection.jsx';
import TreatmentTemplateAdminSection from './TreatmentTemplateAdminSection.jsx';
import InsuranceNoteRuleAdminSection from './InsuranceNoteRuleAdminSection.jsx';
import ProviderSignatureAdminSection from './ProviderSignatureAdminSection.jsx';

import './ClinicalTemplateAdminPage.css';

const SECTIONS = [
  {
    key: 'overview',
    label: 'Overview',
    description: 'Choose which clinical-note configuration you want to manage.',
  },
  {
    key: 'body-parts',
    label: 'Body Parts',
    description: 'Manage the body-part records that other clinical templates belong to.',
  },
  {
    key: 'pain',
    label: 'Pain',
    description: 'Manage pain-scale configuration for each body part.',
  },
  {
    key: 'rom',
    label: 'ROM',
    description: 'Manage range-of-motion fields, sides, units, and validation configuration.',
  },
  {
    key: 'strength',
    label: 'Strength',
    description: 'Manage strength-testing fields and their clinical-note configuration.',
  },
  {
    key: 'goals',
    label: 'Goals',
    description: 'Manage reusable short-term and long-term goal wording and age rules.',
  },
  {
    key: 'special-tests',
    label: 'Special Tests',
    description: 'Manage body-part-specific special-test fields.',
  },
  {
    key: 'palpation',
    label: 'Palpation',
    description: 'Manage body-part-specific palpation findings.',
  },
  {
    key: 'aggravating-factors',
    label: 'Aggravating Factors',
    description: 'Manage universal and body-part-specific aggravating-factor choices for IE/Re-Evaluation.',
  },
  {
    key: 'clinical-content',
    label: 'Clinical Content',
    description: 'Manage reusable assessment, education, rehab potential, plan, and other narrative wording.',
  },
  {
    key: 'treatment',
    label: 'Treatment',
    description: 'Manage reusable treatment wording and treatment-time defaults.',
  },
  {
    key: 'insurance-rules',
    label: 'Insurance Rules',
    description: 'Manage payer-specific note, treatment, copy-forward, and Plan of Care behavior.',
  },
  {
    key: 'provider-signatures',
    label: 'Provider Signatures',
    description: 'Manage configured provider signature records used by the signing workflow.',
  },
];

const SECTION_COMPONENTS = {
  'body-parts': BodyPartTemplateAdminSection,
  pain: PainTemplateAdminSection,
  rom: RomTemplateAdminSection,
  strength: StrengthTemplateAdminSection,
  goals: GoalTemplateAdminSection,
  'special-tests': SpecialTestTemplateAdminSection,
  palpation: PalpationTemplateAdminSection,
  'aggravating-factors': AggravatingFactorTemplateAdminSection,
  'clinical-content': ClinicalContentTemplateAdminSection,
  treatment: TreatmentTemplateAdminSection,
  'insurance-rules': InsuranceNoteRuleAdminSection,
  'provider-signatures': ProviderSignatureAdminSection,
};

const ClinicalTemplateAdminPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const role = localStorage.getItem('role')?.replace('ROLE_', '') || '';
  const requestedSection = searchParams.get('section') || 'overview';

  const activeSection = useMemo(() => {
    return SECTIONS.find((section) => section.key === requestedSection) || SECTIONS[0];
  }, [requestedSection]);

  const canManageClinicalTemplates = ['ADMIN', 'MANAGER'].includes(role);

  if (!canManageClinicalTemplates) {
    return (
      <main className="clinical-template-admin-page">
        <div className="clinical-template-access-denied" role="alert">
          <h1>Clinical Template Setup</h1>
          <p>This page is restricted to ADMIN and MANAGER users.</p>
        </div>
      </main>
    );
  }

  const selectSection = (sectionKey) => {
    if (sectionKey === 'overview') {
      setSearchParams({});
      return;
    }
    setSearchParams({ section: sectionKey });
  };

  const renderOverview = () => (
    <div className="clinical-template-card-grid">
      {SECTIONS.filter((section) => section.key !== 'overview').map((section) => (
        <button
          key={section.key}
          type="button"
          className="clinical-template-card"
          onClick={() => selectSection(section.key)}
        >
          <strong>{section.label}</strong>
          <span>{section.description}</span>
        </button>
      ))}
    </div>
  );

  const renderActiveSection = () => {
    if (activeSection.key === 'overview') {
      return renderOverview();
    }

    const ActiveSectionComponent = SECTION_COMPONENTS[activeSection.key];
    if (!ActiveSectionComponent) {
      return (
        <section className="clinical-template-editor-panel">
          <div className="clinical-template-message error" role="alert">
            The selected clinical-template setup section is not available.
          </div>
        </section>
      );
    }

    return <ActiveSectionComponent />;
  };

  return (
    <main className="clinical-template-admin-page">
      <header className="clinical-template-page-header">
        <div>
          <span className="clinical-template-eyebrow">CLINICAL DOCUMENTATION</span>
          <h1>Clinical Template Setup</h1>
          <p>
            Configure the reusable fields, payer rules, treatment wording, and
            provider signatures used by the clinical-note workflow.
          </p>
        </div>
        <span className="clinical-template-admin-badge">ADMIN / MANAGER</span>
      </header>

      <nav className="clinical-template-tabs" aria-label="Clinical template setup sections">
        {SECTIONS.map((section) => (
          <button
            key={section.key}
            type="button"
            className={activeSection.key === section.key ? 'active' : ''}
            onClick={() => selectSection(section.key)}
            aria-current={activeSection.key === section.key ? 'page' : undefined}
          >
            {section.label}
          </button>
        ))}
      </nav>

      <section className="clinical-template-active-section">
        {renderActiveSection()}
      </section>
    </main>
  );
};

export default ClinicalTemplateAdminPage;
